import React, { FC } from "react";
import { connect } from "react-redux";
import LegalConsentRequired from "./LegalConsentRequired";
import VerificationBlock from "./VerificationBlock/VerificationBlock";
import { VERIFICATION_TARGET, VerificationTarget } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { shouldShowBankId as shouldShowBankIdSelector } from "../../../../authentication/state/selectors";
import * as LOADING_SELECTORS from "../../../../loading/state/selectors";
import { VerificationDeliveryMethod } from "../../../consts";
import { CHECKOUT_SEND_CODE } from "../../../state/actionTypes";
import * as CUSTOMER_INFO_SELECTORS from "../../../state/selectors/customerInfoSelectors";
import {
    getLegalSubscriptionId,
    isReSwitch as isReSwitchSelector,
    isSwitchReplacementAtSubscriptionLevel as isSwitchReplacementAtSubscriptionLevelSelector,
} from "../../../state/selectors/subscriptionsSelectors";
import * as VERIFICATION_SELECTORS from "../../../state/selectors/verificationSelectors";

interface StateProps {
    isLegalOwnerConsentRequired: boolean;
    isLegalOwnerVerified: boolean;
    isProductUserVerified: boolean;
    isReSwitch: boolean;
    isSendingSMSOrEmail: boolean;
    isSwitchReplacementAtSubscriptionLevel: boolean;
    legalOwnerName: string;
    legalOwnerPhone: string;
    shouldShowBankId: boolean;
}

interface PinProps extends StateProps {
    method: VerificationDeliveryMethod;
    onCodeConfirm: (code: string, target: VerificationTarget) => void;
    onWithOrResendPinClick: (target: VerificationTarget, method: string) => void;
}

const Pin: FC<PinProps> = (props) => {
    const {
        isLegalOwnerConsentRequired,
        isLegalOwnerVerified,
        isProductUserVerified,
        isReSwitch,
        isSendingSMSOrEmail,
        method,
        isSwitchReplacementAtSubscriptionLevel,
        shouldShowBankId,
        legalOwnerName,
        legalOwnerPhone,
        onCodeConfirm,
        onWithOrResendPinClick,
    } = props;

    return (
        <>
            {!(isReSwitch && isSwitchReplacementAtSubscriptionLevel) && (
                <VerificationBlock
                    id="verification-block-product-user"
                    verified={isProductUserVerified}
                    target={VERIFICATION_TARGET.TRANSFER_OWNERSHIP_TO_B2C}
                    method={method}
                    onCodeSend={onWithOrResendPinClick}
                    isSendingSMSOrEmail={isSendingSMSOrEmail}
                    onCodeConfirm={onCodeConfirm}
                />
            )}
            {/* Only render if user has verified successfully, but is not legal owner*/}
            {isProductUserVerified && isLegalOwnerConsentRequired && !shouldShowBankId && (
                <LegalConsentRequired
                    isLegalOwnerVerified={isLegalOwnerVerified}
                    isReSwitch={isReSwitch}
                    legalOwnerPhone={legalOwnerPhone}
                    legalOwnerName={legalOwnerName}
                    isSendingSMSOrEmail={isSendingSMSOrEmail}
                    onWithOrResendPinClick={onWithOrResendPinClick}
                    onCodeConfirm={onCodeConfirm}
                    data-di-mask
                />
            )}
        </>
    );
};

const mapStateToProps = (state: RootState, ownProps: { subscriptionId?: string }): StateProps => {
    const subscriptionId = ownProps.subscriptionId || getLegalSubscriptionId(state);

    return {
        // Meta
        isSendingSMSOrEmail: LOADING_SELECTORS.isTypeLoading(state, CHECKOUT_SEND_CODE),

        // Authentication
        shouldShowBankId: shouldShowBankIdSelector(state),

        // Verification
        isProductUserVerified: VERIFICATION_SELECTORS.isProductUserVerified(state),
        isLegalOwnerVerified: VERIFICATION_SELECTORS.isLegalOwnerVerified(state),
        isLegalOwnerConsentRequired: VERIFICATION_SELECTORS.isLegalOwnerConsentRequired(state),

        // Customer Info
        legalOwnerName: CUSTOMER_INFO_SELECTORS.getLegalOwnerFullName(state),
        legalOwnerPhone: CUSTOMER_INFO_SELECTORS.getLegalOwnerPhone(state),

        // Subscriptions Info
        // TODO: We don't know subscription at this point in flow, thus we need to generalize these fields somehow:
        // * isReSwtich => has one subscription with ReSwitch (?)
        // * isSwitchReplacementAtSubscriptionLevel => has one subscription with isSwitchReplacementAtSubscriptionLevel (?)
        // * isSubscriptionBroadband => has one subscription with broadband (?)
        // * isExistingPrepaidKBDSubscription => get this from state.checkout.customerInfo
        isReSwitch: isReSwitchSelector(state, subscriptionId),
        isSwitchReplacementAtSubscriptionLevel: isSwitchReplacementAtSubscriptionLevelSelector(state, subscriptionId),
    };
};

export default connect(mapStateToProps)(Pin);
